import {
  Building,
  PvPanel,
} from "@inrange/building-manager-api-client/models-site";
import axios from "axios";
import { Feature, LineString, Polygon } from "geojson";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { queryOptions } from "./utils";

const pvaHostnameMap: Record<string, string> = {
  prod: "https://7rbvbbr755.execute-api.us-east-1.amazonaws.com",
  staging: "https://nh41zqd7q4.execute-api.us-east-1.amazonaws.com",
  "inrt-1466": "https://32buq9xfy3.execute-api.eu-west-1.amazonaws.com", // dev-martin
  "inrt-1468": "https://32buq9xfy3.execute-api.eu-west-1.amazonaws.com", // dev-martin
  "inrt-2024": "https://32buq9xfy3.execute-api.eu-west-1.amazonaws.com", // dev-martin
};

export const getPVAUrl = (): string => {
  const isProduction = import.meta.env.VITE_KINDE_AUTH_ENV === "PROD";
  if (isProduction) {
    return pvaHostnameMap.prod;
  }

  const branchName = import.meta.env?.VITE_BRANCH_NAME;
  const ticketNo = branchName?.split("-").slice(0, 2).join("-");
  return (!!ticketNo && pvaHostnameMap[ticketNo]) || pvaHostnameMap.staging;
};

async function getPvGeoJson(
  siteId: string,
  panel: { width: number; height: number },
  buildingIds: string
): Promise<{
  panelsData: any;
  isAlignedPanelData: boolean;
  site_image?: SiteImage;
  building_shifts?: Record<string, [number, number]>;
}> {
  const pvaUrl = getPVAUrl();
  const url = `${pvaUrl}/pv-geojson?siteid=${siteId}&panel=${JSON.stringify(panel)}&building_ids=${buildingIds}`;

  const panelsPointerResponse = await axios.get(url, { timeout: 30_000 });

  const panelsUrl = panelsPointerResponse.data?.panels;
  const panelsAlignedUrl = panelsPointerResponse.data?.panels_aligned;
  const siteImage = panelsPointerResponse.data?.site_image;
  const buildingShifts = panelsPointerResponse.data?.building_shifts;

  if (!panelsUrl) {
    return { panelsData: [], isAlignedPanelData: false, site_image: siteImage };
  }

  const isAlignedPanelData = !!(siteImage && panelsAlignedUrl);
  const panelsResponse = await axios.get(
    isAlignedPanelData ? panelsAlignedUrl : panelsUrl,
    {
      timeout: 30_000,
    }
  );

  return {
    panelsData: panelsResponse.data,
    isAlignedPanelData,
    site_image: siteImage,
    building_shifts: buildingShifts,
  };
}

export const usePvGeoJson = (
  siteId: string | undefined,
  panel: PvPanel | undefined,
  buildingIds: string | undefined
): UseQueryResult<{
  panelsData: any;
  isAlignedPanelData: boolean;
  site_image?: SiteImage;
  building_shifts?: Record<string, [number, number]>;
}> => {
  return useQuery(
    ["pv_geo_json", siteId, panel, buildingIds],
    async () => {
      return await getPvGeoJson(siteId!, panel!, buildingIds!);
    },
    queryOptions({
      // Allow query to be mounted multiple times on the same page without re-fetching
      staleTime: 5 * 60 * 1000,
      enabled: !!siteId && !!panel && !!buildingIds,
    })
  );
};

export interface SiteImage {
  image_png: string;
  image_jpg: string;
  cached_at: string;
  bounds: [number, number, number, number];
}

export interface SiteData {
  id: string;
  name: string;
  user: string;
}
export interface PvaData {
  status?: {
    num_buildings?: number;
    num_buildings_triggered?: number;
    num_buildings_packed?: number;
  };
  site_metrics?: {
    total_mass: number;
    net_force: number;
    dead_load: number;
    roof_allocation: number;
    num_panels: number;
    total_pnom: number;
  };
  task?: {
    status: string;
    started_at: string;
  };
  task_metrics?: {
    task_run_success: boolean;
    pv_design_available: boolean;
    // Failure reason from the last run (null on success). Used to report
    // *why* a PVA task failed without re-triggering it.
    error?: string | null;
    start_time: string;
    run_time: number;
    inputs: {
      options: { [key: string]: string };
    };
  };
  debug?: {
    [key: string]: {
      num_panels_before_object_intersection: number;
      num_panels_after_object_intersection: number;
      building_with_objects_image: string;
      building_with_panels_with_objects_image: string;
    };
  };
  geojson?: {
    panels_aligned: string;
    roof_objects_autodetected: string;
  };
  custom_objects?: (Feature<Polygon> | Feature<LineString>)[];
  site_image?: SiteImage;
}

export async function requestPvSystemDesign(
  siteData: SiteData,
  panel: PvPanel,
  buildings: Building[],
  options: Record<string, unknown>,
  customObjects:
    | (Feature<Polygon> | Feature<LineString>)[]
    | undefined = undefined,
  enableTestModePVARequests: boolean
): Promise<{ data: PvaData }> {
  const data = {
    site: siteData,
    panel,
    buildings,
    custom_objects: customObjects,
    options,
  };

  const pvaUrl = enableTestModePVARequests
    ? "https://pva-e2e.com/"
    : getPVAUrl();
  const url = `${pvaUrl}/pv-design?siteid=${siteData.id}`;
  return await axios.post(url, data, { timeout: 30_000 });
}

export const usePvSystemDesign = (
  siteData: SiteData,
  panel: PvPanel | undefined,
  buildings: Building[],
  autoRefresh: boolean,
  enableTestModePVARequests: boolean
): {
  query: UseQueryResult<{ data: PvaData }>;
  requestPvSystemDesign: typeof requestPvSystemDesign;
} => {
  return {
    query: useQuery(
      ["pv_system_design", siteData?.id, panel, autoRefresh, buildings],
      async () => {
        return await requestPvSystemDesign(
          siteData!,
          panel!,
          buildings,
          {},
          undefined,
          enableTestModePVARequests
        );
      },
      {
        enabled: !!siteData && !!panel && !!buildings && buildings?.length > 0,
        refetchInterval: autoRefresh ? 30_000 : false,
        refetchOnWindowFocus: autoRefresh,
      }
    ),
    requestPvSystemDesign,
  };
};

export const usePresignedUrlData = <T>(
  siteId: string,
  urlType: string,
  url: string | undefined
): UseQueryResult<T> => {
  return useQuery(
    [siteId, urlType],
    async (): Promise<T> => {
      const response = await axios.get(url!, { timeout: 30_000 });
      return await response.data;
    },
    {
      enabled: !!url,
    }
  );
};
